import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Application, ApplicationStatus } from '@features/applications';
import ApplicationListItem from '@features/applications/components/ApplicationListItem/ApplicationListItem';
import ApplicationViewModal from '@features/applications/components/ApplicationViewModal/ApplicationViewModal';
import BulkActions from '@features/applications/components/BulkActions/BulkActions';
import { lightGrey, white } from '@styles/colors';
import { authContext } from '@staffedupusa/shared-components';
import EnterpriseApplicationViewModal from '../EnterpriseApplicationViewModal/EnterpriseApplicationViewModal';
import SearchIcon from '@mui/icons-material/Search';

type EnterpriceApplicationsListProps = {
  applications: Application[];
  selectedStatus: ApplicationStatus | string;
  allApplicationsIds: number[];
  pageNo: number;
  isLoading: boolean;
  isWOTCOnly: boolean;
  setIsWOTCOnly: Dispatch<SetStateAction<boolean>>;
  setTriggerChange: Dispatch<SetStateAction<boolean>>;
  alertText: string;
  disableButtons: boolean;
  triggerChange: boolean;
  searchValue: string;
  selectedTag: string;
  totalAplications?: number;
  handleTagsChange: (event: {
    target: { value: React.SetStateAction<string> };
  }) => void;
  setSearchValue: Dispatch<SetStateAction<'All' | string>>;
  handlePageNo: (newPageNo: number) => void;
  selectedCompanyId: string | number;
};

const EnterpriceApplicationsList: FC<EnterpriceApplicationsListProps> = ({
  applications,
  isLoading,
  isWOTCOnly,
  setIsWOTCOnly,
  setTriggerChange,
  triggerChange,
  alertText,
  disableButtons,
  searchValue,
  selectedTag,
  handleTagsChange,
  setSearchValue,
  handlePageNo,
  totalAplications,
  pageNo,
  allApplicationsIds,
  selectedStatus,
  selectedCompanyId,
}) => {
  const { state } = useLocation();
  const containerRef = useRef<HTMLUListElement | null>(null);
  const { authData } = useContext(authContext);
  const [applicationData, setApplicationData] = useState<Application[] | null>(
    null,
  );
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<
    number[]
  >([]);
  const [unselectedApplications, setUnselectedApplications] = useState<
    number[]
  >([]);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [showSelectAll, setShowSelectAll] = useState(false);
  const [showSelectFirstAll, setShowSelectFirstAll] = useState(false);

  const handleToggleAllPositions = () => {
    if (applicationData)
      if (
        selectedApplicationIds.length === allApplicationsIds?.length &&
        allApplicationsIds?.length !== 0
      ) {
        setShowSelectAll(false);
        setShowSelectFirstAll(false);
        setSelectedApplicationIds([]);
      } else if (allApplicationsIds?.length !== 0) {
        setShowSelectAll(true);
        setShowSelectFirstAll(true);
        setSelectedApplicationIds([...allApplicationsIds]);
      } else if (
        applicationData &&
        applicationData?.length > 0 &&
        !showSelectFirstAll
      ) {
        setShowSelectAll(false);
        setShowSelectFirstAll(true);
        setSelectedApplicationIds(applicationData?.map((item) => item.id));
      } else {
        setShowSelectAll(false);
        setShowSelectFirstAll(false);
        setSelectedApplicationIds([]);
      }
  };

  function throttle(func: (...args: any[]) => void, limit: number) {
    let lastFunc: ReturnType<typeof setTimeout>;
    let lastRan: number;
    return (...args: any[]) => {
      if (!lastRan) {
        func(...args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(
          () => {
            if (Date.now() - lastRan >= limit) {
              func(...args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan),
        );
      }
    };
  }

  const handleReset = () => {
    handlePageNo(1);
    setTriggerChange(true);
    setSelectedApplicationIds([]);
    setUnselectedApplications([]);
  };

  const updateRating = (id: number, newRating: number) => {
    if (applicationData && applicationData.length > 0) {
      setApplicationData(
        (prevApps) =>
          prevApps &&
          prevApps.map((app) =>
            app.id === id ? { ...app, rating: newRating } : app,
          ),
      );
    }
    if (selectedApplication && selectedApplication.id === id) {
      setSelectedApplication((prevApp) =>
        prevApp ? { ...prevApp, rating: newRating } : null,
      );
    }
  };

  useEffect(() => {
    if (triggerChange) {
      handlePageNo(1);
      setApplicationData(null);
      setTriggerChange(false);
      setSelectedApplicationIds([]);
      setUnselectedApplications([]);
      setShowSelectFirstAll(false);
      setShowSelectAll(false);
    }
  }, [triggerChange]);

  useEffect(() => {
    if (applications && applications?.length > 0 && !isLoading) {
      const handleScroll = () => {
        if (
          containerRef.current &&
          window.innerHeight + window.scrollY >=
            containerRef.current.scrollHeight
        ) {
          if (applications.length === 10) {
            handlePageNo(pageNo + 1);
          }
        }
      };
      if (pageNo === 1) {
        setApplicationData([...applications]);
      } else if (pageNo > 1) {
        setApplicationData((pre) => [...(pre || []), ...applications]);
      }

      const throttledScroll = throttle(handleScroll, 200);
      window.addEventListener('scroll', throttledScroll);

      return () => {
        window.removeEventListener('scroll', throttledScroll);
      };
    } else if (
      applications &&
      applications?.length == 0 &&
      !isLoading &&
      pageNo === 1
    ) {
      setApplicationData([]);
    }
  }, [applications, isLoading]);

  useEffect(() => {
    if (
      showSelectAll &&
      allApplicationsIds &&
      allApplicationsIds?.length > 0 &&
      showSelectFirstAll
    ) {
      setSelectedApplicationIds([...allApplicationsIds]);
    } else if (
      allApplicationsIds &&
      allApplicationsIds?.length > 0 &&
      showSelectFirstAll
    ) {
      const selectIds = allApplicationsIds?.filter(
        (id) => !unselectedApplications?.includes(id),
      );
      setSelectedApplicationIds([...selectIds]);
    }
  }, [applications, isLoading]);

  useEffect(() => {
    if (state?.application) {
      setSelectedApplication(JSON.parse(state.application));
    }
  }, [state]);

  useEffect(() => {
    if (selectedCompanyId) {
      handleReset();
    }
  }, [selectedCompanyId]);

  return (
    <Stack spacing={3} mt={2}>
      {alertText && alertText !== 'No Applications Found.' && (
        <Alert severity="error">{alertText}</Alert>
      )}

      <Grid
        container
        columnSpacing={{ xs: 0, sm: 2 }}
        rowSpacing={{ xs: 2, lg: 0 }}
        bgcolor={white}
        borderRadius="8px"
        p={3}
        alignItems="center"
        maxWidth="100%"
      >
        <Grid item xs={12} sm={6} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedApplicationIds.length ===
                  (allApplicationsIds?.length !== 0
                    ? allApplicationsIds?.length
                    : applicationData && applicationData?.length)
                }
                indeterminate={
                  selectedApplicationIds.length > 0 &&
                  selectedApplicationIds.length <
                    (allApplicationsIds?.length ?? 0)
                }
                onChange={handleToggleAllPositions}
                disabled={
                  disableButtons ||
                  !Boolean(!applicationData || applicationData?.length > 0)
                }
              />
            }
            label={`Select All ${totalAplications && totalAplications > 0 ? totalAplications : applicationData && applicationData?.length > 0 ? applicationData?.length : ''}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <BulkActions
            disabled={!selectedApplicationIds.length || disableButtons}
            selectedApplicationsIds={selectedApplicationIds}
            handlePageNo={handleReset}
            selectedCompanyId={selectedCompanyId}
            filterData={selectedStatus === 'archived' ? true : false}
            selectedStatus={selectedStatus}
          />
        </Grid>

        <Grid
          width="100%"
          item
          xs={12}
          sm={6}
          lg={6}
          display="flex"
          justifyContent="center"
        >
          <TextField
            variant="outlined"
            size="small"
            color="secondary"
            fullWidth
            placeholder="Search by Applicant Name&hellip;"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="secondary" sx={{ fontSize: '20px' }} />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment
                  position="end"
                  onClick={() => {
                    handlePageNo(1);
                    setSearchValue('');
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <ClearIcon color="secondary" sx={{ fontSize: '18px' }} />
                </InputAdornment>
              ) : null,
            }}
            value={searchValue}
            onChange={(e) => {
              handlePageNo(1);
              setSearchValue(e.target.value);
            }}
            sx={{
              '& input': {
                fontSize: '14px',
              },
            }}
          />
        </Grid>
      </Grid>

      <Stack ref={containerRef} spacing={3} component="ul" sx={{ p: 0 }}>
        {applicationData && applicationData?.length
          ? applicationData.map((application) => (
              <ApplicationListItem
                key={application.id}
                application={application}
                updateRating={updateRating}
                selectedApplications={selectedApplicationIds}
                setSelectedApplications={setSelectedApplicationIds}
                setUnselectedApplications={setUnselectedApplications}
                searchValue={searchValue}
                setSelectedApplication={setSelectedApplication}
                setShowSelectAll={setShowSelectAll}
                disableButtons={disableButtons}
                setApplicationData={setApplicationData}
              />
            ))
          : null}
      </Stack>

      {authData.user?.enterprise ? (
        <EnterpriseApplicationViewModal
          handleClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          searchValue={searchValue}
          setApplication={setSelectedApplication}
          selectedCompanyId={selectedApplication?.job_listing?.company?.id}
          setApplicationData={setApplicationData}
          updateRating={updateRating}
          handlePageNo={(newPageNo) => {
            setTriggerChange(true);
            handlePageNo(newPageNo);
          }}
        />
      ) : (
        <ApplicationViewModal
          handleClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          searchValue={searchValue}
          setApplication={setSelectedApplication}
          setApplicationData={setApplicationData}
          updateRating={updateRating}
          handlePageNo={(newPageNo) => {
            setTriggerChange(true);
            handlePageNo(newPageNo);
          }}
        />
      )}
      {isLoading ? (
        <Box textAlign="center">
          <CircularProgress data-testid="loader" color="success" />
        </Box>
      ) : !applicationData || (!applicationData.length && !isLoading) ? (
        <Alert severity="info" style={{ marginTop: '0' }}>
          There are no applications with the selected options.
        </Alert>
      ) : null}
    </Stack>
  );
};

export default EnterpriceApplicationsList;
